import React, {useEffect, useState} from 'react';
import Layout from '@gakki/components/Layout';
import PageToolBar from '@gakki/components/PageToolBar';
import {Checkbox} from 'components/form-items';
import AddNewLocationButton from './components/add-new-location-button';
import LocationCard from './components/location-card/location-card';
import {StepStatus, useAppContext} from '@gakki/context/app-context';
import {APIClient, useAuthContext, useClient} from '@gakki/context/auth-context';
import {
  useConfirmSetting,
  useSaveSetting,
  CheckInSettingModel,
} from '@gakki/api/check-in';
import {FailDialog} from '@gakki/components/Dialog';
import usePageData from '@common/hooks/usePageData';
import {toast} from 'react-toastify';
import LocationForm from './components/location-form';
import {useForm, FormProvider} from 'react-hook-form';
import {BaseResponse} from '@gakki/api/types';
import {LocationFormField} from './types';
import {useMutation} from 'react-query';

const defaultValues: CheckInSettingModel = {
  enableFacePass: false,
  enableGPSCheckIn: false,
};

function CheckInSetting() {
  const {data, status, init, edit, remove, updatedTime} =
    usePageData<CheckInSettingModel>('check-in-setting', defaultValues);
  const {
    changeStep,
    step,
    setIsLoading,
    isLoading,
    save,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();
  const client = useClient() as APIClient;
  const {companyId} = useAuthContext();
  const saveData = (data: CheckInSettingModel) => {
    return client.put(`attendance/company/${companyId}/checkin/setting`, {
      data: {
        ...data,
        checkInGeoLocations: data.enableGPSCheckIn
          ? data.checkInGeoLocations
          : [],
      },
    });
  }
  
  const confirmData = () => {
    return client.put(`attendance/company/${companyId}/checkin/setting/confirm`);
  }
  const {mutateAsync: saveMutate} = useSaveSetting({
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutateAsync: handleConfirm} = useMutation(
    (data: CheckInSettingModel) => saveData(data)
    .then(() => refetchEnablePY('next', step + 1))
    .then(async () => {
      try {
        await confirmData()
        setIsLoading(false)
        changeStep(step + 1);
        confirm()
        remove();
      } catch (e: any) {
        throw new Error(e);
      }
    }),
    {
      onMutate: () => setIsLoading(true),
      onError: (error: {code: number; message: string}) => {
        setIsLoading(false)
        if (error.code !== 500) {
          toast.error(error.message || '打卡設定儲存失敗', {
            hideProgressBar: true,
            position: 'top-center',
            autoClose: false
          });
        }
      },
    },
  );
  const [showFailDialog, setShowFailDialog] = React.useState(false);
  const [showForm, setShowForm] = useState(false);
  const formMethods = useForm<LocationFormField>();
  const {reset} = formMethods;

  useEffect(() => {
    if (data === null || status === StepStatus.init) {
      setIsLoading(true);
      client
        .get<BaseResponse<CheckInSettingModel>>(
          `attendance/company/${companyId}/checkin/setting`,
        )
        .then(d => {
          init(d.data);
          setIsLoading(false);
        });
      return;
    }
  }, []);

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      saveMutate(data)
    }
  }, [saveAndGoStep])

  return (
    <React.Fragment>
      <Layout.MainContent>
        <PageToolBar pageTitle={'打卡設定'} />
        <div
          className={
            'grow h-full p-[22px] rounded-[3px] border border-[#d4d9dc]'
          }
        >
          <p>請選擇貴公司欲使用之打卡方式(可複選)：</p>
          <div className={'mb-[22px] w-[fit-content]'}>
            <Checkbox
              data-test-id={'enable-facepass-check-in'}
              labelName={'人臉辨識'}
              onChange={e => {
                edit({
                  ...data,
                  enableFacePass: e.target.checked,
                });
              }}
              checked={data.enableFacePass}
            />
          </div>
          <div className={'w-[fit-content]'}>
            <Checkbox
              data-test-id={'enable-gps-check-in'}
              labelName={'GPS定位打卡：'}
              onChange={e =>
                edit({
                  ...data,
                  enableGPSCheckIn: e.target.checked,
                })
              }
              checked={data.enableGPSCheckIn}
            />
          </div>
          {data.enableGPSCheckIn && (
            <div className={'mt-[25px] flex gap-[23px] items-start flex-wrap'}>
              {data.checkInGeoLocations?.map((setting, idx) => {
                return (
                  <LocationCard
                    key={idx}
                    locationInfo={setting}
                    onEdit={() => {
                      reset({
                        ...setting,
                        idx,
                        ipAddresses:
                          setting.ipAddresses?.map(i => ({value: i})) || [],
                        enableWebIPCheckIn: setting.ipAddresses?.length ? true : false
                      });

                      setShowForm(true);
                    }}
                    onRemove={() => {
                      const locations = data.checkInGeoLocations || [];
                      edit({
                        ...data,
                        checkInGeoLocations: [
                          ...locations.slice(0, idx),
                          ...locations.slice(idx + 1, locations.length),
                        ],
                      });
                    }}
                  />
                );
              })}
              <AddNewLocationButton
                onClick={() => {
                  reset({ipAddresses: [{value: ''}]});
                  setShowForm(true);
                }}
              />
            </div>
          )}
        </div>
        <FormProvider {...formMethods}>
          <LocationForm
            onClose={() => setShowForm(false)}
            onConfirm={location => {
              const locations = data.checkInGeoLocations || [];
              let idx = locations.length;
              if (location.idx) {
                idx = location.idx;
              }

              edit({
                ...data,
                checkInGeoLocations: [
                  ...locations.slice(0, idx),
                  {
                    ...location,
                    ipAddresses: location.ipAddresses?.map(i => i.value),
                  },
                  ...locations.slice(idx + 1, locations.length),
                ],
              });
              setShowForm(false);
            }}
            isOpen={showForm}
          />
        </FormProvider>
      </Layout.MainContent>
      <Layout.Footer
        updatedTime={updatedTime}
        showNextButton
        showPrevButton
        showSaveButton
        showUpdatedTime
        disabledSaveButton={status !== StepStatus.edit || isLoading}
        disabledPrevButton={status === StepStatus.edit || isLoading}
        disabledNextButton={isLoading}
        onSave={async () => {
          try {
            await saveMutate(data);
          } catch (error: any) {
            if (error && error.code && error.code === 500) {
              return;
            }
            toast.error(
              `儲存失敗${
                error && error.message ? `：${error.message}。` : '。'
              }`,
              {
                position: 'top-center',
                hideProgressBar: true,
                pauseOnFocusLoss: false,
                autoClose: false,
              },
            );
          }
        }}
        onNext={async () => {
          if (
            data.enableGPSCheckIn &&
            (!data.checkInGeoLocations || data.checkInGeoLocations.length === 0)
          ) {
            return setShowFailDialog(true);
          }
          await handleConfirm(data);
        }}
        onPrev={async () => {
          await onPrev();
          remove();
        }}
      />
      <FailDialog
        isOpen={showFailDialog}
        onConfirm={() => setShowFailDialog(false)}
        onCancel={() => setShowFailDialog(false)}
      />
    </React.Fragment>
  );
}

export default CheckInSetting;
